/* eslint-disable arrow-parens */
import { useState, useRef, useEffect, useContext } from "react";
import { AgGridColumn, AgGridReact } from "ag-grid-react";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import getAgGridHeight from "../../../../../../../../../../utils/commonFunctions/getAgGridHeight";
import initCapitalize from "../../../../../../../../../../utils/commonFunctions/initCapitalize";
import { dateConverterToUSFormat } from "../../../../../../../../../../utils/commonFunctions/date";
import { HistoryContactInformationProps } from "../../../../../../../../../../@types/member.types";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Button, Menu, MenuItem, Tooltip, IconButton } from "@material-ui/core";
import { api } from "../../../../../../../../../../utils/api";
import { AuthContext } from "../../../../../../../../../../contexts";
const DependentHistoryContactInformationGrid = ({ gridData }: HistoryContactInformationProps): JSX.Element => {
	// console.log("DependentHistoryContactInformationGrid=====>", gridData);
	const [agGridAPI, setAgGridAPI] = useState(null);
	const [gridHeight, setGridHeight] = useState("400px");
	const [agGridColumnAPI, setAgGridColumnAPI] = useState(null);
	const agGridRef = useRef(null);

	// console.log("Dependent History Contact Grid", gridData[0]);

	const handleAgGridReady = (params: any) => {
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const HandleSSNCellRenderer = ({ value, data }: { value: string; data: any }) => {
		const [isVisible, setIsVisible] = useState(false);
		const { user } = useContext(AuthContext);

		// First check if value exists
		if (!value) return <div></div>;

		// Format SSN with dashes
		const formattedSSN = value.replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3");

		const formatSSN = (ssn: string, visible = false) => {
			if (!ssn) return "";
			if (visible) return ssn;
			const last4 = ssn.slice(-4);
			return `XXX-XX-${last4}`;
		};

		const handleToggleVisibility = async (e: React.MouseEvent) => {
			e.stopPropagation();

			if (!isVisible) {
				try {
					const viewSSNAddLogPayload = {
						member_id: data._id,
						member_role: data.role
					};
					await api.viewSSNLog.viewSSNAddLog(viewSSNAddLogPayload);
				} catch (error) {
					console.error("Error logging SSN view:", error);
					return;
				}
			}
			setIsVisible(!isVisible);
		};

		const displayedSSN = isVisible ? formattedSSN : formatSSN(formattedSSN);

		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
					width: "100%",
					height: "100%",
					justifyContent: "space-between"
				}}
			>
				<span>{displayedSSN}</span>
				<IconButton
					onClick={handleToggleVisibility}
					size="small"
					style={{
						padding: "4px",
						color: isVisible ? "red" : "green"
					}}
					title={isVisible ? "Hide SSN" : "Show SSN"}
				>
					{isVisible ? <FaEye /> : <FaEyeSlash />}
				</IconButton>
			</div>
		);
	};

	const handleZIPCellRenderer = ({ value }: { value: string }) => {
		let ZIP = value;
		if (ZIP.length === 9) ZIP = value.substring(0, 5) + "-" + value.substring(5);
		return <div>{ZIP}</div>;
	};

	const handleCapitalizeCellRenderer = ({ value }: { value: string }) => {
		const capitalize = initCapitalize(value);
		return <div>{capitalize}</div>;
	};

	const handleLastUpdateNameCellRenderer = ({ value }: { value: any }) => {
		const lastUpdate = value.first_name + " " + value.last_name;
		return <div>{lastUpdate}</div>;
	};

	const handleDateCellRenderer = ({ value }: { value: string }) => dateConverterToUSFormat(value);

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	useEffect(() => {
		const _gridHeight = getAgGridHeight(gridData.length);
		setGridHeight(_gridHeight);
	}, [gridData.length]);

	return (
		<div className="history-paycheck-info-grid-container glb-tab-grid-container">
			<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
				<AgGridReact
					ref={agGridRef}
					rowData={gridData}
					onGridReady={handleAgGridReady}
					onFirstDataRendered={handleFirstDataRendered}
					rowSelection={"Single"}
					animateRows
					suppressColumnVirtualisation={true}
					defaultColDef={{
						resizable: true,
						filter: "agTextColumnFilter",
						floatingFilter: true,
						floatingFilterComponentParams: { suppressFilterButton: true }
					}}
					frameworkComponents={{
						handleDateCellRenderer: handleDateCellRenderer,
						handleSSNCellRenderer: HandleSSNCellRenderer,
						handleZIPCellRenderer: handleZIPCellRenderer,
						handleCapitalizeCellRenderer: handleCapitalizeCellRenderer,
						handleLastUpdateNameCellRenderer: handleLastUpdateNameCellRenderer
					}}
				>
					<AgGridColumn
						field="dependent_number"
						headerName="WLT Dependent Number"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn field="email" headerName="Email" suppressSizeToFit={true}></AgGridColumn>
					<AgGridColumn
						field="SSN"
						// headerName="ALT-ID"
						headerName="SSN"
						cellRenderer="handleSSNCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="address_line_1"
						headerName="Address Line 1"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="address_line_2"
						headerName="Address Line 2"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn field="city" headerName="City" suppressSizeToFit={true}></AgGridColumn>
					<AgGridColumn field="state" headerName="State" suppressSizeToFit={true}></AgGridColumn>
					<AgGridColumn field="country" headerName="Country" suppressSizeToFit={true}></AgGridColumn>
					<AgGridColumn
						field="ZIP"
						headerName="ZIP Code"
						cellRenderer="handleZIPCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="contact_label"
						headerName="Contact Label"
						cellRenderer="handleCapitalizeCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="phone_extension"
						headerName="Phone Extension"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="phone_number"
						headerName="Phone Number"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="created_date"
						headerName="Created On"
						cellRenderer="handleDateCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="created_by_details"
						headerName="Created By"
						cellRenderer="handleLastUpdateNameCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="updatedAt"
						headerName="Updated On"
						cellRenderer="handleDateCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
					<AgGridColumn
						field="updated_by_details"
						headerName="Updated By"
						cellRenderer="handleLastUpdateNameCellRenderer"
						suppressSizeToFit={true}
					></AgGridColumn>
				</AgGridReact>
			</div>
		</div>
	);
};

export default DependentHistoryContactInformationGrid;
